import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Target, Mail, MessageCircle, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PhoneInput from "@/components/PhoneInput";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const [method, setMethod] = useState("email"); // email | mobile
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(""); // E.164
  const [step, setStep] = useState("request");
  const [code, setCode] = useState("");
  const [channel, setChannel] = useState("");
  const [busy, setBusy] = useState(false);

  const identifier = method === "email" ? email : phone;

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-otp", { identifier });
      setChannel(data.channel);
      setStep("verify");
      toast.success(`If enrolled, a code was sent via ${data.channel === "email" ? "email" : "WhatsApp"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { identifier, code });
      login(data.token, data.user);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-emerald-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #10b981 0, transparent 40%), radial-gradient(circle at 80% 70%, #f59e0b 0, transparent 45%)" }} />
        <div className="relative z-10 flex items-center gap-3">
          <img src="/logo.png?v=3" alt="Health Coach Marathon" className="h-12 w-12 rounded-xl object-contain bg-white p-1" />
          <span className="font-display text-xl font-extrabold tracking-tight">Health Coach Marathon</span>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-tight">
            Turn your 90-day income goal into a daily game plan.
          </h1>
          <p className="text-emerald-100/90 text-lg max-w-md">
            Set your target once. We break it into people to reach, Session 0s to run, and clients to close — every single week.
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-md pt-4">
            {[["Reach", "People/wk"], ["Session 0s", "Booked/wk"], ["Earn", "₹ target"]].map(([a, b]) => (
              <div key={a} className="rounded-xl bg-white/10 backdrop-blur px-4 py-3 border border-white/10">
                <div className="font-display font-bold">{a}</div>
                <div className="text-xs text-emerald-100/80">{b}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-emerald-200/70 text-sm">For Health Coaches • Sneh World</div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                    className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/logo.png?v=3" alt="Health Coach Marathon" className="h-10 w-10 rounded-lg object-contain bg-white border border-border" />
            <span className="font-display text-lg font-extrabold">Health Coach Marathon</span>
          </div>

          <h2 className="font-display text-3xl font-bold tracking-tight mb-1">Coach sign in</h2>
          <p className="text-muted-foreground mb-8">Get a one-time code on your email or WhatsApp.</p>

          {step === "request" ? (
            <form onSubmit={requestOtp} className="space-y-5">
              <Tabs value={method} onValueChange={(v) => { setMethod(v); }} className="w-full">
                <TabsList className="grid grid-cols-2 w-full mb-2" data-testid="coach-method-tabs">
                  <TabsTrigger value="email" data-testid="coach-method-email"><Mail className="h-4 w-4 mr-2" /> Email</TabsTrigger>
                  <TabsTrigger value="mobile" data-testid="coach-method-mobile"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp</TabsTrigger>
                </TabsList>
                <TabsContent value="email">
                  <Label htmlFor="email">Email address</Label>
                  <Input id="email" data-testid="coach-login-email-input" type="email" className="mt-1.5"
                         placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                </TabsContent>
                <TabsContent value="mobile">
                  <Label>WhatsApp number</Label>
                  <div className="mt-1.5">
                    <PhoneInput testidPrefix="coach-login" onChange={setPhone} />
                  </div>
                </TabsContent>
              </Tabs>
              <Button type="submit" disabled={busy || !identifier}
                      data-testid="coach-request-otp-btn"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send me a code <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-4">
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                {channel === "email" ? <Mail className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
                Code sent to <span className="font-medium text-foreground">{identifier}</span>
              </p>
              <div>
                <Label htmlFor="code">6-digit code</Label>
                <Input id="code" data-testid="coach-otp-input" className="mt-1.5 tracking-[0.5em] font-mono-num text-lg"
                       inputMode="numeric" maxLength={6} value={code}
                       onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required />
              </div>
              <Button type="submit" disabled={busy || code.length !== 6}
                      data-testid="coach-verify-otp-btn"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
              </Button>
              <button type="button" className="text-sm text-muted-foreground hover:text-foreground w-full text-center"
                      onClick={() => { setStep("request"); setCode(""); }}>
                Use a different email / number
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
